import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Button, Form, message, Select, Image, Switch, Modal } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { v4 as uuidv4 } from "uuid";
import { ApolloError } from "@apollo/client";

import { useAddOrganizationMutation } from "graphql/mutations/add-organization.generated";
import { useGetJurisdictionsQuery } from "graphql/queries/get-jurisdictions.generated";
import { Organizations_Insert_Input } from "graphql/generated/graphql";

import UploadPhotoModal from "components/upload-photo-modal/upload-photo-modal";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import routeNames from "constants/route-names";
import { OrganizationType, UserRoleValue, VendorCustomersStatus } from "constants/field-values";
import { NAV_PROFILE_OPTIONS } from "constants/constants";
import { useAuth } from "context/AuthContext";
import { uploadToStorage } from "utils/files-management";

const OrganizationAdd: React.FC = () => {
	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();
	const { refreshToken } = useAuth();
	const [name, setName] = useState<string>("");
	const [contactName, setContactName] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [displayPrice, setDisplayPrice] = useState<boolean>(false);
	const [type, setType] = useState<OrganizationType | string>("");
	const [jurisdictionId, setJurisdictionId] = useState<string>("");
	const [navProfile, setNavProfile] = useState<string>("");

	// Only an Authority organization enforces a jurisdiction; it is what scopes its
	// users to the inspections and plan reviews they review.
	const isAuthority = type === OrganizationType.Authority;

	const { data: jurisdictionsData, loading: loadingJurisdictions } = useGetJurisdictionsQuery({
		skip: !isAuthority,
		fetchPolicy: "cache-first",
		onError: useCallback((error: ApolloError) => console.log("getJurisdictions Error: ", error), [])
	});

	const [addOrgPhotoModalOpen, setAddOrgPhotoModalOpen] = useState<boolean>(false);
	const [orgPhotoThumbUrl, setOrgPhotoThumbUrl] = useState<string>("");
	const [orgPhotoFile, setOrgPhotoFile] = useState<RcFile | null>(null);
	const [orgPhotoUrl, setOrgPhotoUrl] = useState<string>("");
	const [loadError, setLoadError] = useState<string | null>(null);
	const [uploading, setUploading] = useState(false);

	const [organizationInputCompleted, setOrganizationInputCompleted] = useState(false);

	const handleOnCompleteAddOrganizationMutation = useCallback(
		async (data: any) => {
			if (refreshToken) {
				await refreshToken();
			}

			navigate(routeNames.ADMIN_ORGANIZATIONS);
		},
		[refreshToken]
	);

	const handleOnErrorAddOrganizationMutation = useCallback((error: ApolloError) => {
		console.log("useAddOrganizationMutation Error: ", error);
		setLoadError(error.message);
	}, []);

	const [addOrganizationMutation, { data, loading, error }] = useAddOrganizationMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorAddOrganizationMutation,
		onCompleted: handleOnCompleteAddOrganizationMutation
	});

	const uploadOrganizationImg = useCallback(async () => {
		if (orgPhotoFile) {
			try {
				setUploading(true);
				const uniqueID = uuidv4();

				const bucketName = "organization-photos/";

				const fileName = `organization-photo-${name}-${uniqueID}-${Math.floor(Date.now() / 1000)}-${orgPhotoFile.name}`;

				const { publicUrl } = await uploadToStorage(orgPhotoFile, () => {}, `${bucketName}/${fileName}`);

				setOrgPhotoUrl(publicUrl);
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
			} finally {
				setUploading(false);
			}
		}

		return;
	}, [orgPhotoFile, name]);

	const handleOnRemovePhoto = useCallback(() => {
		setOrgPhotoFile(null);
		setOrgPhotoThumbUrl("");
	}, []);

	const handleOnConfirmPhoto = useCallback((file: any, url: string) => {
		setOrgPhotoThumbUrl(url);
		setOrgPhotoFile(file);
	}, []);

	const handleOnSubmit = useCallback(async () => {
		if (orgPhotoFile) {
			await uploadOrganizationImg();
		}

		setOrganizationInputCompleted(true);
	}, [orgPhotoFile]);

	const uploadOrganization = useCallback(async () => {
		const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

		let organizationInput: Organizations_Insert_Input = {
			name,
			contact_name: contactName,
			email,
			display_price: displayPrice,
			workspace_id: loggedUserValue.workspace_id,
			app_base_url: baseUrl
		};

		if (isAuthority && jurisdictionId) {
			organizationInput.jurisdiction_id = jurisdictionId;
		}

		if (navProfile && loggedUserValue.role === UserRoleValue.admin_user) {
			organizationInput.nav_profile = navProfile;
		}

		if (type) {
			organizationInput.type = type;
		} else {
			if (loggedUserValue.organization?.type === OrganizationType.Vendor) {
				organizationInput.type = OrganizationType.Customer;
			} else {
				organizationInput.type = OrganizationType.Vendor;
			}
		}

		if (loggedUserValue.id) {
			organizationInput.created_by_user_id = loggedUserValue.id;

			if (loggedUserValue.organization_id) {
				organizationInput.collaborators = {
					data: [
						{
							user_id: loggedUserValue.id,
							user_organization_id: loggedUserValue.organization_id
						}
					]
				};

				// Only a vendor takes on the new organization as its customer. 
				// Linking from a Customer or Authority organization would instead hand that organization —
				// and every user in it — access to the new one.
				if (loggedUserValue.organization?.type === OrganizationType.Vendor) {
					organizationInput.vendors = {
						data: [
							{
								created_by_user_id: loggedUserValue.id,
								updated_by_user_id: loggedUserValue.id,
								vendor_org_id: loggedUserValue.organization_id,
								status: VendorCustomersStatus.active
							}
						]
					};
				}
			}
		}

		if (orgPhotoUrl) {
			organizationInput.organizationImageURL = orgPhotoUrl;
		}

		addOrganizationMutation({
			variables: {
				input: organizationInput
			}
		});
	}, [name, contactName, email, loggedUserValue, displayPrice, orgPhotoUrl, type, isAuthority, jurisdictionId, navProfile]);

	useEffect(() => {
		if (organizationInputCompleted) {
			uploadOrganization();
		}
	}, [organizationInputCompleted]);

	const isLoading = uploading || loading;

	return (
		<div className="add-product-screen-container">
			<span className="add-product-page-title">{"New Customer Organization"}</span>
			<Form className="add-product-form" autoComplete="off">
				<span className="add-product-form-title">{"Organization Details"}</span>

				{UserRoleValue.admin_user === loggedUserValue.role ? (
					<Form.Item
						className="login-form-item"
						rules={[
							{
								required: true,
								message: "Please select an Organization Type."
							}
						]}
						required
						validateTrigger="onBlur"
					>
						<Select
							value={type || undefined}
							className="add-product-input-select"
							onChange={value => setType(value)}
							options={Object.values(OrganizationType).map(element => {
								return { value: element, label: element };
							})}
							placeholder="Organization Type"
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									style={{ width: "1em", height: "1em" }}
									className="chevron-button"
								/>
							}
						/>
					</Form.Item>
				) : null}

				{isAuthority ? (
					<Form.Item
						className="login-form-item"
						rules={[{ required: true, message: "Please select the jurisdiction." }]}
						required
						validateTrigger="onBlur"
					>
						<Select
							value={jurisdictionId || undefined}
							className="add-product-input-select"
							onChange={value => setJurisdictionId(value)}
							options={(jurisdictionsData?.jurisdictions ?? []).map(jurisdiction => ({
								value: jurisdiction.id,
								label: jurisdiction.name
							}))}
							placeholder="Jurisdiction"
							showSearch
							optionFilterProp="label"
							allowClear
							disabled={isLoading || loadingJurisdictions}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									style={{ width: "1em", height: "1em" }}
									className="chevron-button"
								/>
							}
						/>
					</Form.Item>
				) : null}

				{UserRoleValue.admin_user === loggedUserValue.role ? (
					<Form.Item className="login-form-item">
						<Select
							value={navProfile || undefined}
							className="add-product-input-select"
							onChange={value => setNavProfile(value || "")}
							options={[...NAV_PROFILE_OPTIONS]}
							placeholder="Navigation profile (empty: decided by the role of each user)"
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									style={{ width: "1em", height: "1em" }}
									className="chevron-button"
								/>
							}
						/>
					</Form.Item>
				) : null}

				<Form.Item
					className="login-form-item"
					rules={[{ required: true, message: "Please input the organization name." }]}
					required
					validateTrigger="onBlur"
				>
					<Input
						name="name"
						placeholder="Name"
						className="add-product-input-pair"
						value={name}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setName(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<Input
						name="name"
						placeholder="Contact Name (Optional)"
						className="add-product-input-pair"
						value={contactName}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setContactName(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				<Form.Item className="login-form-item" validateTrigger="onBlur">
					<Input
						name="name"
						placeholder="Contact Email (Optional)"
						className="add-product-input-pair"
						value={email}
						autoComplete="off"
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							setEmail(event.target.value);
						}}
						allowClear
						disabled={isLoading}
					/>
				</Form.Item>

				{/* Authorities don't order devices, so customer-facing pricing doesn't apply to them. */}
				{UserRoleValue.customer_manager !== loggedUserValue.role && !isAuthority && (
					<div className="login-form-item">
						<span className="switch-label">Display customer-facing pricing for device orders</span>
						<Switch checked={displayPrice} onChange={checked => setDisplayPrice(checked)} disabled={isLoading} />
					</div>
				)}

				<div className="organization-button-container">
					<Button className="upload-product-button" onClick={() => setAddOrgPhotoModalOpen(true)} disabled={isLoading}>
						Upload Organization Logo (Optional)
					</Button>
				</div>

				{orgPhotoThumbUrl ? (
					<div className="organization-button-container">
						<Image width={170} src={orgPhotoThumbUrl} />{" "}
						<img
							alt={`remove-organization-photo-icon`}
							src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
							className="organization-photo-action-icon"
							onClick={() => {
								if (!isLoading) {
									handleOnRemovePhoto();
								}
							}}
						/>
					</div>
				) : null}

				<UploadPhotoModal
					open={addOrgPhotoModalOpen}
					onCancel={() => setAddOrgPhotoModalOpen(false)}
					uploadMethod={handleOnConfirmPhoto}
					aspectSlider
					disableCrop
				/>
			</Form>

			<div className="add-product-button-container">
				<Button className="admin-add-product-button" htmlType="submit" onClick={handleOnSubmit} disabled={isLoading}>
					Save
				</Button>
			</div>
			<Modal
				title="Error"
				open={!!loadError}
				onOk={() => setLoadError(null)}
				onCancel={() => setLoadError(null)}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{
					style: {
						backgroundColor: "#8B0000",
						borderColor: "#8B0000",
						color: "#fff"
					}
				}}
			>
				<p>{loadError}</p>
			</Modal>

			<LoadingOverlay isLoading={isLoading} />
		</div>
	);
};

export default OrganizationAdd;
